import type { ListDetailsLevel, ListSortKey, SortDirection } from "@/domain/interventionList";
import type { PeriodId } from "@/domain/systemAnalysisTrend";
import type { DetailsPosition, Tab } from "./urlState";

/** What the details panel next to the matrix currently shows. */
export type Panel =
  | { kind: "none" }
  | { kind: "details"; itemId: string }
  | { kind: "edit"; itemId: string }
  | { kind: "create" };

/** True while an intervention is being created or edited. */
export function isEditorPanel(panel: Panel): boolean {
  return panel.kind === "edit" || panel.kind === "create";
}

/** "overTime" is the experimental trend view (Admin → Experimental features). */
export type SystemViewMode = "single" | "overTime";

export interface SystemUiState {
  mode: SystemViewMode;
  /** Years shown in the "over time" view. */
  period: PeriodId;
  analysisId: string;
  locationId: string;
  criterionId: string | null;
  domainId: string | null;
  showAbout: boolean;
  showScale: boolean;
  expandedDomains: ReadonlySet<string>;
  expandedDescriptions: ReadonlySet<string>;
}

export interface UiState {
  /** True after configured startup defaults have been resolved (individual mode). */
  startupReady: boolean;
  tab: Tab;
  viewId: string;
  language: string;
  /** Empty set = all locations. */
  selectedRegions: ReadonlySet<string>;
  /** See `ChipSelections` in domain/filters. */
  chipSelections: Readonly<Record<string, ReadonlySet<string>>>;
  search: string;
  showTitles: boolean;
  showIds: boolean;
  listDetailsLevel: ListDetailsLevel;
  listSort: { key: ListSortKey; direction: SortDirection };
  statsFiltersVisible: boolean;
  detailsPosition: DetailsPosition;
  detailsWidth: number;
  columnLayoutOverride: string | null;
  panel: Panel;
  /** Matrix hidden while editing an intervention opened from the list. */
  editOnly: boolean;
  system: SystemUiState;
}

export type StartupUiValues = Pick<
  UiState,
  "tab" | "viewId" | "language" | "showTitles" | "showIds" | "detailsPosition" | "detailsWidth"
> & { systemLocationId: string };

export type UiAction =
  | { type: "initializeStartup"; values: StartupUiValues }
  | { type: "setTab"; tab: Tab }
  | { type: "setView"; viewId: string }
  | { type: "setLanguage"; language: string }
  | { type: "setRegions"; regions: ReadonlySet<string> }
  | { type: "setChipSelection"; key: string; codes: ReadonlySet<string> }
  | { type: "setSearch"; search: string }
  | { type: "toggleTitles" }
  | { type: "toggleIds" }
  | { type: "cycleListDetails" }
  | { type: "sortList"; key: ListSortKey }
  | { type: "toggleStatsFilters" }
  | { type: "toggleDetailsPosition" }
  | { type: "setDetailsWidth"; width: number }
  | { type: "showDetails"; itemId: string }
  | { type: "editIntervention"; itemId: string; editOnly: boolean }
  | { type: "createIntervention" }
  | { type: "closePanel" }
  | { type: "setSystemMode"; mode: SystemViewMode }
  | { type: "setSystemPeriod"; period: PeriodId }
  | { type: "selectSystemAnalysis"; analysisId: string }
  | { type: "selectSystemLocation"; locationId: string }
  | { type: "selectSystemCriterion"; domainId: string; criterionId: string }
  | { type: "closeSystemDetail" }
  | { type: "toggleSystemAbout" }
  | { type: "toggleSystemScale" }
  | { type: "toggleSystemDomain"; domainId: string }
  | { type: "toggleSystemDescription"; domainId: string };

export const DETAILS_LAYOUT = {
  defaultPosition: "right" as DetailsPosition,
  defaultWidth: 500,
  minDetailsWidth: 420,
  minMatrixWidth: 520,
  minSystemMainWidth: 320,
};

function toggleInSet(set: ReadonlySet<string>, value: string): ReadonlySet<string> {
  const next = new Set(set);
  if (!next.delete(value)) next.add(value);
  return next;
}

const resetSystemSelection = (system: SystemUiState): SystemUiState => ({
  ...system,
  criterionId: null,
  domainId: null,
  expandedDomains: new Set(),
  expandedDescriptions: new Set(),
});

export function uiReducer(state: UiState, action: UiAction): UiState {
  switch (action.type) {
    case "initializeStartup": {
      // Apply all startup values in one reducer update: no transient fallback
      // view can be mirrored into the address bar between dispatches.
      const { systemLocationId, ...values } = action.values;
      return {
        ...state,
        ...values,
        startupReady: true,
        system: { ...state.system, locationId: systemLocationId },
      };
    }
    case "setTab":
      return {
        ...state,
        tab: action.tab,
        statsFiltersVisible: false,
        editOnly: action.tab === "matrix" ? false : state.editOnly,
      };
    case "setView":
      return {
        ...state,
        viewId: action.viewId,
        panel: state.panel.kind === "details" ? { kind: "none" } : state.panel,
      };
    case "setLanguage":
      return { ...state, language: action.language };
    case "setRegions":
      return { ...state, selectedRegions: action.regions };
    case "setChipSelection":
      return { ...state, chipSelections: { ...state.chipSelections, [action.key]: action.codes } };
    case "setSearch":
      return { ...state, search: action.search };
    case "toggleTitles":
      return { ...state, showTitles: !state.showTitles };
    case "toggleIds":
      return { ...state, showIds: !state.showIds };
    case "cycleListDetails":
      return { ...state, listDetailsLevel: ((state.listDetailsLevel + 1) % 3) as ListDetailsLevel };
    case "sortList": {
      const same = state.listSort.key === action.key;
      return {
        ...state,
        listSort: {
          key: action.key,
          direction: same && state.listSort.direction === "asc" ? "desc" : "asc",
        },
      };
    }
    case "toggleStatsFilters":
      return { ...state, statsFiltersVisible: !state.statsFiltersVisible };
    case "toggleDetailsPosition":
      return { ...state, detailsPosition: state.detailsPosition === "right" ? "below" : "right" };
    case "setDetailsWidth":
      return { ...state, detailsWidth: action.width };
    case "showDetails":
      return { ...state, panel: { kind: "details", itemId: action.itemId }, editOnly: false };
    case "editIntervention":
      return {
        ...state,
        panel: { kind: "edit", itemId: action.itemId },
        editOnly: action.editOnly,
      };
    case "createIntervention":
      return { ...state, tab: "matrix", panel: { kind: "create" }, editOnly: true };
    case "closePanel":
      return { ...state, panel: { kind: "none" }, editOnly: false };
    case "setSystemMode":
      return { ...state, system: { ...resetSystemSelection(state.system), mode: action.mode } };
    case "setSystemPeriod":
      return { ...state, system: { ...state.system, period: action.period } };
    case "selectSystemAnalysis":
      return {
        ...state,
        system: { ...resetSystemSelection(state.system), analysisId: action.analysisId },
      };
    case "selectSystemLocation":
      return {
        ...state,
        system: { ...resetSystemSelection(state.system), locationId: action.locationId },
      };
    case "selectSystemCriterion":
      return {
        ...state,
        system: { ...state.system, domainId: action.domainId, criterionId: action.criterionId },
      };
    case "closeSystemDetail":
      return { ...state, system: { ...state.system, domainId: null, criterionId: null } };
    case "toggleSystemAbout":
      return { ...state, system: { ...state.system, showAbout: !state.system.showAbout } };
    case "toggleSystemScale":
      return { ...state, system: { ...state.system, showScale: !state.system.showScale } };
    case "toggleSystemDomain":
      return {
        ...state,
        system: {
          ...state.system,
          expandedDomains: toggleInSet(state.system.expandedDomains, action.domainId),
        },
      };
    case "toggleSystemDescription":
      return {
        ...state,
        system: {
          ...state.system,
          expandedDescriptions: toggleInSet(state.system.expandedDescriptions, action.domainId),
        },
      };
  }
}
